import { useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import {
  TrendingUp, Timer, PiggyBank, AlertTriangle, PackageX, FileText,
  Sparkles, Cpu, BarChart3, Check, Wallet,
} from "lucide-react";
import { costPerInspectionText } from "../lib/smartInspectApi";

// Sprint 9A — qualitative strategic benefits (display only; NOT part of ROI math).
const STRATEGIC_BENEFITS = [
  { key: "operational-intelligence", title: "Operational Intelligence", icon: Sparkles,
    items: ["PRIVA AI Insights", "Damage Trends", "Carrier Performance", "Root Cause Analysis"] },
  { key: "connected-operations", title: "Connected Operations", icon: Cpu,
    items: ["SAP Integration", "Claims Evidence", "Cross-Team Visibility", "Carrier & Customer Collaboration"] },
  { key: "management", title: "Management", icon: BarChart3,
    items: ["Executive Dashboards", "Better Compliance", "Digital Audit Trail", "Continuous Improvement"] },
];

const fmtCur = (cur, v) => {
  if (v === null || v === undefined) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD", maximumFractionDigits: 0 }).format(v);
  } catch { return `${cur} ${Number(v).toLocaleString()}`; }
};
const fmtNum = (v, d = 0) => (v === null || v === undefined ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));

function Card({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const tones = {
    default: "border-slate-200", positive: "border-emerald-200 bg-emerald-50/40", muted: "border-amber-200 bg-amber-50/40",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border p-3.5 bg-white min-w-0 h-full flex flex-col ${tones[tone]}`}>
      <div className="flex items-start gap-1.5 text-slate-400 text-[10px] uppercase tracking-wide font-semibold min-h-[2.4rem]">
        <Icon className="w-3.5 h-3.5 shrink-0 mt-px" /> <span>{label}</span>
      </div>
      <div className="text-sm sm:text-base xl:text-lg font-bold text-slate-900 leading-tight tracking-tight tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1 leading-snug">{sub}</div>}
    </div>
  );
}

export function SmartInspectResults({ result, onEditStep, onRecalculate, embedded = false, hideZeroSavings = false }) {
  const [scenario, setScenario] = useState(result?.scenario || "expected");
  const cur = result?.preferred_currency || "USD";

  const sc = useMemo(() => {
    const s = result?.scenarios?.[scenario];
    return s || { monthly_results: result?.monthly_results, annual_results: result?.annual_results, three_year_results: result?.three_year_results, additional_potential_benefits: result?.additional_potential_benefits };
  }, [result, scenario]);

  if (!result || result.calculation_status === "calculation_failed") {
    return (
      <div data-testid="si-results-failed" className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
        We couldn't calculate your ROI. Please review your inputs and try again.
        <div className="mt-3"><Button data-testid="si-results-recalc-failed" onClick={() => onRecalculate?.()}>Recalculate</Button></div>
      </div>
    );
  }

  // Sprint 9E — Annual basis only (Monthly/Annual toggle removed).
  const M = sc.annual_results || {};
  const A = sc.annual_results || {};
  const T = sc.three_year_results || {};
  const warnings = result.warnings || [];

  const blocked = !!result.roi_blocked;
  const capacityExceeded = !!result.capacity_exceeded;
  const customPricing = !!result.custom_pricing;
  const reason = result.roi_unavailable_reason;
  const reasonSub = reason ? `Reason: ${reason}` : undefined;
  // Warnings other than the capacity / custom-pricing reasons (already shown in dedicated banners).
  const otherWarnings = warnings.filter((w) => !/^Selected package supports/.test(w) && !/^Custom pricing selected/.test(w));

  // Executive summary
  const effortPct = (() => {
    const c = Number(A.current_time_per_inspection || 0);
    const s = Number(A.smartinspect_time_per_inspection || 0);
    if (!c || c <= 0) return null;
    return Math.max(0, Math.round(((c - s) / c) * 100));
  })();
  const execSummary = blocked
    ? "Operational savings have been calculated successfully. Select a valid commercial package to calculate ROI, payback and investment returns."
    : `Based on your current inspection process, SmartInspect is estimated to reduce inspection effort by ${effortPct ?? 0}%, delivering approximately ${fmtCur(cur, A.total_direct_savings)} in annual gross operational savings.`;

  const cmpRows = [
    { label: "Inspection Time", current: M.current_time_per_inspection, si: M.smartinspect_time_per_inspection, type: "min" },
    { label: "Inspection Hours", current: M.current_hours, si: M.smartinspect_hours, type: "hrs" },
    { label: "Total Process Cost", current: M.current_process_cost, si: M.smartinspect_process_cost, type: "cur" },
  ];
  const fmtMetric = (type, v) => {
    if (v === null || v === undefined) return "—";
    if (type === "min") return `${fmtNum(v, 1)} min`;
    if (type === "hrs") return `${fmtNum(v, 0)} hrs`;
    return fmtCur(cur, v);
  };
  // Sprint 9C — savings breakdown: Labour, Paper, Photo, SAP, Storage & IT,
  // Claims Administration, Audit Preparation. Equipment is intentionally hidden;
  // it is only appended when non-zero so the displayed lines always reconcile to
  // Total Gross Direct Savings.
  const savRows = [
    ["Inspection Labour", M.inspection_labour_savings],
    ["Administration & Reporting", M.administration_savings],
    ["Paper & Printing", M.paper_savings],
    ["Photo Handling", M.photo_savings],
    ["SAP / ERP Entry", M.sap_savings],
    ["Storage & IT", M.storage_it_savings],
    ["Claims & Evidence Preparation", M.claims_savings],
  ];
  if (Number(M.equipment_savings) > 0) savRows.push(["Equipment", M.equipment_savings]);
  const snap = result.package_pricing_snapshot || {};

  const naCur = (v) => (blocked ? "Not Available" : fmtCur(cur, v));
  const roiValue = blocked ? "Not Available" : (A.first_year_roi_percent === null || A.first_year_roi_percent === undefined ? "Not Available" : `${fmtNum(A.first_year_roi_percent, 1)}%`);
  const paybackValue = blocked ? "Not Available" : (A.payback_months === null || A.payback_months === undefined ? "No Payback" : `${fmtNum(A.payback_months, 1)} months`);

  return (
    <div data-testid="si-results" className="space-y-8">
      {/* Section 1 — Executive ROI Summary (hero) */}
      <div data-testid="si-exec-hero" className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#F5FAFB] to-white p-6 sm:p-8 shadow-sm">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#025979] font-semibold mb-3">Executive ROI Summary</div>
        <p data-testid="si-exec-summary" className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl">
          {execSummary}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-7" data-testid="si-headline-cards">
          <Card testid="si-card-annual-savings" icon={PiggyBank} label="Annual Savings" value={fmtCur(cur, A.total_direct_savings)} tone="positive" />
          <Card testid="si-card-first-year-investment" icon={Wallet} label="First-Year Investment" value={A.first_year_investment == null ? "Not Available" : fmtCur(cur, A.first_year_investment)} tone={A.first_year_investment == null ? "muted" : "default"} />
          <Card testid="si-card-roi" icon={TrendingUp} label="ROI %" value={roiValue} sub={blocked ? reasonSub : undefined} tone={blocked ? "muted" : "default"} />
          <Card testid="si-card-payback" icon={Timer} label="Payback Period" value={paybackValue} sub={blocked ? reasonSub : "First-Year Investment ÷ monthly gross savings"} tone={blocked ? "muted" : "default"} />
          <Card testid="si-card-3yr" icon={TrendingUp} label="Three-Year Net Benefit" value={naCur(T.net_benefit)} sub={blocked ? reasonSub : undefined} tone={blocked ? "muted" : "default"} />
        </div>
      </div>

      {/* Package Capacity Exceeded banner */}
      {capacityExceeded && (
        <div data-testid="si-banner-capacity" className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-center gap-2 text-amber-900 text-sm font-semibold mb-2"><PackageX className="w-4 h-4" /> Package Capacity Exceeded</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mb-3">
            <div><div className="text-[10px] uppercase text-amber-700">Estimated Monthly Volume</div><div className="font-semibold text-amber-900">{fmtNum(result.estimated_monthly_volume, 0)} / month</div></div>
            <div><div className="text-[10px] uppercase text-amber-700">Selected Package Capacity</div><div className="font-semibold text-amber-900">{result.selected_package_unlimited ? "Unlimited" : `${fmtNum(result.selected_package_limit, 0)} / month`}</div></div>
            <div><div className="text-[10px] uppercase text-amber-700">Recommended Package</div><div className="font-semibold text-amber-900">{result.recommended_package || "Contact us for a custom plan"}</div></div>
          </div>
          {onEditStep && <Button data-testid="si-banner-capacity-edit" onClick={() => onEditStep(3)} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-8">Edit SmartInspect Solution</Button>}
        </div>
      )}

      {/* Custom Pricing Selected banner */}
      {customPricing && !capacityExceeded && (
        <div data-testid="si-banner-custom" className="rounded-lg border border-sky-300 bg-sky-50 p-4">
          <div className="flex items-center gap-2 text-sky-900 text-sm font-semibold mb-1"><FileText className="w-4 h-4" /> Custom Pricing Selected</div>
          <p className="text-xs text-sky-800 mb-3">Your selected package or deployment uses custom pricing, so ROI, payback and investment returns cannot be calculated automatically. Operational savings below remain valid.</p>
          <div className="flex flex-wrap gap-2">
            {onEditStep && <Button data-testid="si-banner-custom-edit" variant="outline" onClick={() => onEditStep(3)} className="border-[#025979] text-[#025979] rounded-sm text-xs h-8">Edit SmartInspect Solution</Button>}
            {!embedded && (
              <a href="/warehousex/smartintegrations/smartinspect#demo" data-testid="si-banner-custom-demo">
                <Button className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-8">Request Demo</Button>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Other warnings */}
      {otherWarnings.length > 0 && (
        <div data-testid="si-results-warnings" className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 text-amber-800 text-sm font-semibold mb-1"><AlertTriangle className="w-4 h-4" /> Please review</div>
          <ul className="list-disc pl-5 text-xs text-amber-700 space-y-0.5">{otherWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}

      {/* Scenario controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="si-scenario-toggle">
          {["conservative", "expected", "optimistic"].map((s) => (
            <button key={s} data-testid={`si-scenario-${s}`} onClick={() => setScenario(s)}
              className={`px-3 py-1.5 text-xs capitalize ${scenario === s ? "bg-[#025979] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{s}</button>
          ))}
        </div>
      </div>

      {/* Section 2 — Current Process vs SmartInspect */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="si-comparison">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Current Process vs SmartInspect (Annual)</h3>
          <span className="text-xs text-slate-400">Annual Inspection Volume: <b className="text-slate-600">{fmtNum(A.inspection_volume, 0)}</b></span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase text-slate-400 border-b border-slate-200">
              <th className="text-left py-1.5">Metric</th>
              <th className="text-right">Current</th>
              <th className="text-right">SmartInspect</th>
              <th className="text-right">Savings</th>
            </tr>
          </thead>
          <tbody>
            {cmpRows.map((r) => {
              const cu = Number(r.current);
              const si = Number(r.si);
              const hasVals = Number.isFinite(cu) && Number.isFinite(si);
              const sav = hasVals ? cu - si : null;
              return (
                <tr key={r.label} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 text-slate-600">{r.label}</td>
                  <td className="text-right text-slate-700">{fmtMetric(r.type, r.current)}</td>
                  <td className="text-right font-medium text-[#025979]">{fmtMetric(r.type, r.si)}</td>
                  <td className="text-right font-bold text-slate-800">{sav === null ? "—" : fmtMetric(r.type, sav)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Section 3 — Where the Savings Come From (executive summary) */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="si-savings-breakdown">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Where the Savings Come From (Annual)</h3>
        <div className="divide-y divide-slate-100">
          {(hideZeroSavings ? savRows.filter(([, v]) => Number(v) > 0) : savRows).map(([label, v]) => (
            <div key={label} className="flex items-center justify-between py-2.5 text-sm" data-testid={`si-sav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}>
              <span className="text-slate-600">{label}</span>
              <span className="font-medium text-slate-800">{fmtCur(cur, v)}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between py-3 mt-3 bg-emerald-50 rounded-lg px-3.5" data-testid="si-direct-total">
          <span className="font-semibold text-emerald-800 text-sm">Total Gross Direct Savings</span>
          <span className="font-bold text-emerald-800 text-base">{fmtCur(cur, M.total_direct_savings)}</span>
        </div>
      </div>

      {/* Section 4 — Selected SmartInspect Solution */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="si-plan">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Selected SmartInspect Solution</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Item label="SmartInspect Package" value={result.selected_package_name || "—"} />
          <Item label="Monthly Inspection Limit" value={snap.unlimited_inspections ? "Unlimited monthly inspections" : (snap.monthly_inspection_limit != null ? `${fmtNum(snap.monthly_inspection_limit, 0)} / month` : "—")} />
          <Item label="Monthly Subscription"
            value={snap.custom_pricing || snap.billing_model === "Custom" ? "Custom Pricing" : fmtCur(cur, sc.monthly_results?.package_subscription)}
            sub={
              <>
                {!(snap.custom_pricing || snap.billing_model === "Custom") && A.package_subscription != null && (
                  <span className="block" data-testid="si-plan-annual-sub">{fmtCur(cur, A.package_subscription)} / year</span>
                )}
                <span className="block">{costPerInspectionText({ price: sc.monthly_results?.package_subscription, monthlyLimit: snap.monthly_inspection_limit, unlimited: snap.unlimited_inspections, custom: snap.custom_pricing || snap.billing_model === "Custom", currencyCode: cur })}</span>
              </>
            }
            testid="si-plan-cpi" />
          <Item label="Estimated Implementation Investment" value={fmtCur(cur, A.implementation_investment ?? A.deployment_cost)} />
          <Item label="First-Year Investment" testid="si-plan-first-year-investment"
            value={A.first_year_investment == null ? "Not Available" : fmtCur(cur, A.first_year_investment)}
            sub="Subscription + recurring add-ons + implementation + one-time add-ons" />
        </div>
        {(A.addon_lines || []).length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="si-plan-addons">
            <div className="text-[10px] uppercase text-slate-400 font-semibold mb-2">Optional Add-ons</div>
            <div className="divide-y divide-slate-100">
              {(A.addon_lines || []).map((a) => (
                <div key={a.name} className="flex items-center justify-between py-2 text-sm" data-testid={`si-plan-addon-${(a.name||"").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="text-slate-600">{a.name}</span>
                  <span className="font-medium text-slate-800">
                    {a.custom ? "Custom / quote required"
                      : a.one_time != null ? `${fmtCur(cur, a.one_time)} one-time`
                      : `${fmtCur(cur, a.annual)} / year`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Section 5 — Additional Business Benefits (qualitative; not in ROI) */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="si-strategic-benefits">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Additional Business Benefits</h3>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 bg-slate-100 rounded-full px-2.5 py-1">Not Included in ROI</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {STRATEGIC_BENEFITS.map((g) => (
            <div key={g.key} data-testid={`si-benefit-group-${g.key}`} className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
              <div className="flex items-center gap-2 text-[#025979] text-xs font-semibold mb-3">
                <g.icon className="w-4 h-4" /> {g.title}
              </div>
              <ul className="space-y-1.5">
                {g.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-[13px] text-slate-600">
                    <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-500 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Actions — customer-facing only (hidden in embedded consultant view) */}
      {!embedded && (
      <div className="flex flex-wrap gap-2 pt-1" data-testid="si-results-actions">
        <Button data-testid="si-edit-company" variant="outline" onClick={() => onEditStep(1)}>Edit Company Information</Button>
        <Button data-testid="si-edit-operations" variant="outline" onClick={() => onEditStep(2)}>Edit Current Operations</Button>
        <Button data-testid="si-edit-plan" variant="outline" onClick={() => onEditStep(3)}>Edit SmartInspect Solution</Button>
        <Button
          data-testid="si-start-new"
          variant="outline"
          className="border-[#025979] text-[#025979]"
          onClick={() => {
            try { localStorage.removeItem("smartinspect_roi_resume"); } catch { /* ignore */ }
            window.location.href = "/warehousex/smartintegrations/smartinspect/roi";
          }}
        >
          Start New ROI Assessment
        </Button>
      </div>
      )}
    </div>
  );
}

function Item({ label, value, sub, testid }) {
  return (
    <div>
      <div className="text-[10px] text-slate-400 uppercase">{label}</div>
      <div className="text-slate-800 font-semibold">{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5" data-testid={testid}>{sub}</div>}
    </div>
  );
}
